import { HomeReview, ReviewTemplate } from '../types';
import { filterCategoriesForReview, pruneEmptyCategories } from '../utils/historicalTemplateFilters';

interface ReviewSnapshotDatabase {
  getReviewTemplateSnapshot?: (reviewId: number) => Promise<ReviewTemplate | null>;
  saveReviewTemplateSnapshot?: (reviewId: number, template: ReviewTemplate) => Promise<void>;
}

interface ReviewSnapshotOfflineReviewService {
  cacheOnlineReview?: (review: HomeReview) => Promise<void>;
}

interface ReviewSnapshotOnlineApi {
  getReviewSnapshot: (reviewId: number) => Promise<HomeReview>;
}

interface ReviewSnapshotLogger {
  warn: (...args: any[]) => void;
}

export interface ReviewSnapshotCacheDependencies {
  database?: ReviewSnapshotDatabase | null;
  offlineReviewService?: ReviewSnapshotOfflineReviewService | null;
  onlineApi: ReviewSnapshotOnlineApi;
  getTemplates: () => Promise<ReviewTemplate[]>;
  ensureOnlineState: () => Promise<boolean>;
  getIsOnline: () => boolean;
  isWeb: () => boolean;
  logger?: ReviewSnapshotLogger;
}

export class ReviewSnapshotCacheService {
  private readonly database?: ReviewSnapshotDatabase | null;
  private readonly offlineReviewService?: ReviewSnapshotOfflineReviewService | null;
  private readonly onlineApi: ReviewSnapshotOnlineApi;
  private readonly getTemplates: () => Promise<ReviewTemplate[]>;
  private readonly ensureOnlineState: () => Promise<boolean>;
  private readonly getIsOnline: () => boolean;
  private readonly isWeb: () => boolean;
  private readonly logger: ReviewSnapshotLogger;

  constructor(dependencies: ReviewSnapshotCacheDependencies) {
    this.database = dependencies.database;
    this.offlineReviewService = dependencies.offlineReviewService;
    this.onlineApi = dependencies.onlineApi;
    this.getTemplates = dependencies.getTemplates;
    this.ensureOnlineState = dependencies.ensureOnlineState;
    this.getIsOnline = dependencies.getIsOnline;
    this.isWeb = dependencies.isWeb;
    this.logger = dependencies.logger || console;
  }

  async preloadReviewSnapshots(reviews: HomeReview[]): Promise<void> {
    if (!Array.isArray(reviews) || reviews.length === 0 || this.isWeb()) {
      return;
    }

    if (!(await this.ensureOnlineState())) {
      return;
    }

    for (const review of reviews) {
      if (!this.getIsOnline()) {
        break;
      }

      if (!review?.id) {
        continue;
      }

      try {
        const snapshotExists =
          this.database?.getReviewTemplateSnapshot &&
          (await this.database.getReviewTemplateSnapshot(review.id));

        if (snapshotExists) {
          continue;
        }

        await this.fetchAndCacheReviewSnapshot(review);
      } catch (error) {
        this.logger.warn('[ReviewService] Failed to preload snapshot for review', review.id, error);
        const fallback = await this.buildFallbackTemplateSnapshot(review);
        if (fallback && this.database?.saveReviewTemplateSnapshot) {
          try {
            await this.database.saveReviewTemplateSnapshot(review.id, fallback);
          } catch (snapshotError) {
            this.logger.warn('[ReviewService] Failed to save fallback snapshot:', snapshotError);
          }
        }
      }
    }
  }

  private async fetchAndCacheReviewSnapshot(review: HomeReview): Promise<void> {
    if (!review?.id || !(await this.ensureOnlineState())) {
      if (!review?.id) {
        this.logger.warn('[ReviewService] Skipping snapshot fetch - missing review ID');
      } else {
        this.logger.warn('[ReviewService] Skipping snapshot fetch - offline');
      }
      return;
    }

    try {
      const detail = await this.onlineApi.getReviewSnapshot(review.id);

      if (this.offlineReviewService?.cacheOnlineReview) {
        await this.offlineReviewService.cacheOnlineReview(detail);
      }
    } catch (error) {
      this.logger.warn('[ReviewService] Snapshot fetch failed for review', review.id, error);
      throw error;
    }
  }

  private async buildFallbackTemplateSnapshot(review: HomeReview): Promise<ReviewTemplate | null> {
    try {
      const templates = await this.getTemplates();
      const template = templates.find(t => t.id === review.templateId);
      if (!template) {
        this.logger.warn('[ReviewService] No cached template for fallback snapshot', {
          reviewId: review.id,
          templateId: review.templateId,
        });
        return null;
      }

      const snapshot = JSON.parse(JSON.stringify(template));
      const reviewCreatedAt = review.createdAt ? new Date(review.createdAt) : new Date();
      snapshot.categories = filterCategoriesForReview(snapshot.categories || [], reviewCreatedAt);
      snapshot.categories = pruneEmptyCategories(snapshot.categories || []);
      return snapshot;
    } catch (error) {
      this.logger.warn('[ReviewService] Failed to build fallback snapshot:', error);
      return null;
    }
  }
}
